const SITE_URL = "https://shoppingdobem.com.br";
const DEFAULT_LOGO = `${SITE_URL}/shopping-do-bem-search-logo.png`;
const ORG_ID = `${SITE_URL}/#organization`;

function JsonLdScript({ data }: { data: Record<string, unknown> }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

export function OrganizationJsonLd() {
  return (
    <JsonLdScript
      data={{
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": ORG_ID,
        name: "Shopping do Bem",
        url: SITE_URL,
        logo: DEFAULT_LOGO,
        description: "Ajude projetos sociais através do Shopping do Bem. O Cashback solidário garante que toda compra vire doação.",
        foundingDate: "2025-01",
        email: "adm@shoppingdobem.com.br",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Quadra 2 Conjunto A-4, Casa 26",
          addressLocality: "Brasília, Sobradinho",
          addressRegion: "DF",
          postalCode: "73015-104",
          addressCountry: "BR",
        },
        sameAs: [
          "https://www.instagram.com/shoppingdobem.br/",
          "https://www.facebook.com/shoppingdobem",
        ],
      }}
    />
  );
}

export function WebSiteJsonLd() {
  return (
    <JsonLdScript
      data={{
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Shopping do Bem",
        url: SITE_URL,
        publisher: { "@id": ORG_ID },
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${SITE_URL}/marcas?q={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      }}
    />
  );
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

export function BreadcrumbJsonLd({ items }: { items: BreadcrumbItem[] }) {
  return (
    <JsonLdScript
      data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: item.name,
          item: item.url,
        })),
      }}
    />
  );
}

interface ArticleJsonLdProps {
  title: string;
  description: string;
  url: string;
  imageUrl?: string;
  images?: string[];
  publishedAt?: string;
  modifiedAt?: string;
  authorName?: string;
  inLanguage?: string;
  articleSection?: string;
}

export function ArticleJsonLd({ title, description, url, imageUrl, images, publishedAt, modifiedAt, authorName, inLanguage, articleSection }: ArticleJsonLdProps) {
  const imageField = (images && images.length > 0)
    ? images
    : imageUrl
      ? [imageUrl]
      : undefined;
  return (
    <JsonLdScript
      data={{
        "@context": "https://schema.org",
        "@type": "Article",
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        headline: title,
        description,
        url,
        ...(imageField && { image: imageField }),
        ...(publishedAt && { datePublished: publishedAt }),
        ...(modifiedAt && { dateModified: modifiedAt }),
        ...(inLanguage && { inLanguage }),
        ...(articleSection && { articleSection }),
        author: {
          "@type": "Person",
          name: authorName || "Shopping do Bem",
        },
        publisher: { "@id": ORG_ID },
      }}
    />
  );
}

interface Coupon {
  id?: string;
  title: string;
  discount_type: string;
  discount_value: number | null;
  code: string | null;
  type: string;
  description?: string | null;
  destination_url?: string | null;
  expires_at?: string | null;
}

interface ProductJsonLdProps {
  name: string;
  description: string;
  url: string;
  imageUrl?: string;
  coupons?: Coupon[];
}

export function ProductJsonLd({ name, description, url, imageUrl, coupons }: ProductJsonLdProps) {
  const offers = (coupons || [])
    .filter((c) => c.discount_value)
    .slice(0, 10)
    .map((c) => ({
      "@type": "Offer",
      name: c.title,
      description: c.code ? `Código: ${c.code}` : c.title,
      priceCurrency: "BRL",
      price: "0",
      availability: "https://schema.org/InStock",
      discount: c.discount_type === "percentage" ? `${c.discount_value}%` : `R$${c.discount_value}`,
    }));

  return (
    <JsonLdScript
      data={{
        "@context": "https://schema.org",
        "@type": "Product",
        name,
        description,
        url,
        ...(imageUrl && { image: imageUrl }),
        ...(offers.length > 0 && { offers }),
      }}
    />
  );
}

interface NgoJsonLdProps {
  name: string;
  description: string;
  url: string;
  logoUrl?: string;
}

export function NgoJsonLd({ name, description, url, logoUrl }: NgoJsonLdProps) {
  return (
    <JsonLdScript
      data={{
        "@context": "https://schema.org",
        "@type": "NGO",
        name,
        description,
        url,
        ...(logoUrl && { logo: logoUrl }),
      }}
    />
  );
}

/* ============================================================
 * Cuponomia-style schemas for brand pages (/marca/:slug)
 * Three blocks: BreadcrumbList (Thing format), WebPage, Store + AggregateRating
 * ============================================================ */

interface CuponomiaBreadcrumbItem {
  name: string;
  url: string;
}

/**
 * Emits BreadcrumbList in the exact Cuponomia format,
 * where each ListItem.item is a Thing with id+name (not the
 * generic Google format with name/item at the ListItem level).
 */
export function CuponomiaBreadcrumbJsonLd({ items }: { items: CuponomiaBreadcrumbItem[] }) {
  return (
    <JsonLdScript
      data={{
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((it, i) => ({
          "@type": "ListItem",
          position: i + 1,
          item: {
            "@type": "Thing",
            "@id": it.url,
            name: it.name,
          },
        })),
      }}
    />
  );
}

interface WebPageJsonLdProps {
  url: string;
  name: string;
  /** ISO 8601 date (YYYY-MM-DD) — when the page content was last reviewed */
  lastReviewed?: string;
}

export function WebPageJsonLd({ url, name, lastReviewed }: WebPageJsonLdProps) {
  return (
    <JsonLdScript
      data={{
        "@context": "http://schema.org",
        "@type": "WebPage",
        url,
        name,
        ...(lastReviewed && { lastReviewed }),
      }}
    />
  );
}

interface StoreJsonLdProps {
  pageId?: string;
  /** Official store website (e.g. https://www.carrefour.com.br/) */
  url?: string;
  name: string;
  image?: string;
  description?: string | null;
  ratingValue?: number | null;
  ratingCount?: number | null;
}

/**
 * Emits Store schema in the Cuponomia format, including an
 * AggregateRating array (only when both rating value and count are provided).
 * Maps to "Empresas locais", "Organização" and "Snippets de avaliação"
 * in Google's Rich Results Test.
 */
export function StoreJsonLd({ pageId, url, name, image, description, ratingValue, ratingCount }: StoreJsonLdProps) {
  const hasRating =
    typeof ratingValue === "number" &&
    ratingValue > 0 &&
    typeof ratingCount === "number" &&
    ratingCount >= 1;

  return (
    <JsonLdScript
      data={{
        "@context": "http://schema.org",
        "@type": "Store",
        ...(pageId && { "@id": pageId }),
        ...(url && { url }),
        name,
        ...(image && { image }),
        ...(description && { description }),
        ...(hasRating && {
          aggregateRating: [
            {
              "@type": "AggregateRating",
              name,
              ratingValue: Number(ratingValue),
              ratingCount: Number(ratingCount),
              bestRating: 5,
              worstRating: 1,
            },
          ],
        }),
      }}
    />
  );
}

interface FaqPageJsonLdProps {
  items: { question: string; answer: string }[];
}

/**
 * Emits FAQPage schema for rich results. Returns null if no items.
 */
export function FaqPageJsonLd({ items }: FaqPageJsonLdProps) {
  if (!items || items.length === 0) return null;
  return (
    <JsonLdScript
      data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: items.map((it) => ({
          "@type": "Question",
          name: it.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: it.answer,
          },
        })),
      }}
    />
  );
}

/* ============================================================
 * Brand page schemas — connected graph:
 * Organization (brand) + WebSite + Brand + OfferCatalog
 * ============================================================ */

interface BrandOrganizationJsonLdProps {
  brandName: string;
  brandSlug: string;
  storeUrl?: string;
  logoUrl?: string | null;
  description?: string | null;
  codeCount: number;
  offerCount: number;
  totalCount: number;
  updatedAtISO?: string;
  coupons?: Coupon[];
}

export function BrandOrganizationJsonLd({
  brandName,
  brandSlug,
  storeUrl,
  logoUrl,
  description,
  codeCount,
  offerCount,
  totalCount,
  updatedAtISO,
  coupons,
}: BrandOrganizationJsonLdProps) {
  const pageUrl = `${SITE_URL}/marca/${brandSlug}`;
  const dateStr = updatedAtISO ? ` Atualizada em ${updatedAtISO}.` : "";
  const organizationId = `${SITE_URL}/#organization`;
  const websiteId = `${SITE_URL}/#website`;
  const collectionPageId = `${pageUrl}#collectionpage`;
  const offerCatalogId = `${pageUrl}#offercatalog`;

  const parts: string[] = [];
  if (codeCount > 0) parts.push(`${codeCount} ${codeCount === 1 ? "cupom" : "cupons"}`);
  if (offerCount > 0) parts.push(`${offerCount} ${offerCount === 1 ? "oferta" : "ofertas"}`);
  const catalogDescription = parts.length > 0
    ? `Temos ${parts.join(" e ")} ${totalCount === 1 ? "ativo" : "ativas"} hoje para ${brandName} no Shopping do Bem.${dateStr}`
    : `Confira ${brandName} no Shopping do Bem.${dateStr}`;
  const collectionName = `Cupons e ofertas ${brandName}`;

  const formatDiscount = (c: Coupon): string | undefined => {
    if (c.discount_type === "percentage" && c.discount_value) return `${c.discount_value}%`;
    if (c.discount_type === "fixed" && c.discount_value) return `R$${c.discount_value}`;
    if (c.discount_type === "shipping") return "Frete Grátis";
    if (c.discount_type === "freebie") return "Brinde";
    if (c.discount_type === "promo") return "Promoção";
    return undefined;
  };

  const buildOfferNode = (c: Coupon) => {
    const discount = formatDiscount(c);
    const desc = c.description && c.description.trim().length > 0
      ? c.description
      : "Válido em todo o site.";
    const isCode = c.type === "code";
    const category = isCode ? "Coupon" : "Deal";
    const brandPageUrl = `${SITE_URL}/marca/${brandSlug}`;
    const fragment = isCode && c.code
      ? `coupon-${c.code.toLowerCase().replace(/\s+/g, "")}`
      : `offer-${c.id ?? ""}`;
    const offerId = `${brandPageUrl}#${fragment}`;
    return {
      "@type": "Offer",
      "@id": offerId,
      name: c.title,
      description: desc,
      ...(discount && { discount }),
      category,
      ...(isCode && c.code && { couponCode: c.code }),
      url: storeUrl || pageUrl,
      ...(c.expires_at && { validThrough: c.expires_at }),
    };
  };

  const codeCoupons = (coupons || []).filter((c) => c.type === "code");
  const offerCoupons = (coupons || []).filter((c) => c.type === "offer");

  const itemLists = [
    codeCount > 0 && {
      "@type": "ItemList",
      name: `${codeCount} Cupons de Código`,
      numberOfItems: codeCount,
      itemListElement: codeCoupons.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: buildOfferNode(c),
      })),
    },
    offerCount > 0 && {
      "@type": "ItemList",
      name: `${offerCount} Ofertas Ativas`,
      numberOfItems: offerCount,
      itemListElement: offerCoupons.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: buildOfferNode(c),
      })),
    },
  ].filter(Boolean);

  return (
    <JsonLdScript
      data={{
        "@context": "https://schema.org",
        "@graph": [
          // 3. CollectionPage — landing page de cupons da marca
          {
            "@type": "CollectionPage",
            "@id": collectionPageId,
            name: collectionName,
            description: catalogDescription,
            url: pageUrl,
            inLanguage: "pt-BR",
            ...(updatedAtISO && { dateModified: updatedAtISO }),
            isPartOf: { "@id": websiteId },
            publisher: { "@id": organizationId },
            about: { "@id": `${pageUrl}#store` },
            mainEntity: { "@id": offerCatalogId },
          },
          // 5. OfferCatalog
          {
            "@type": "OfferCatalog",
            "@id": offerCatalogId,
            name: collectionName,
            description: catalogDescription,
            numberOfItems: totalCount,
            itemListElement: itemLists,
          },
        ],
      }}
    />
  );
}

/* ============================================================
 * Brand review snippet (block 3)
 * Root is AggregateRating so Google renders it as a Review Snippet;
 * itemReviewed points at the brand Organization being rated.
 * Renders null unless ratingCount > 5 (business rule).
 * ============================================================ */

interface BrandReviewSnippetJsonLdProps {
  brandName: string;
  codeCount: number;
  offerCount: number;
  updatedAtISO?: string;
  ratingValue?: number | null;
  ratingCount?: number | null;
}

export function BrandReviewSnippetJsonLd({
  brandName,
  codeCount,
  offerCount,
  updatedAtISO,
  ratingValue,
  ratingCount,
}: BrandReviewSnippetJsonLdProps) {
  const hasRating =
    typeof ratingValue === "number" &&
    ratingValue > 0 &&
    typeof ratingCount === "number" &&
    ratingCount > 5;
  if (!hasRating) return null;

  const dateStr = updatedAtISO ? ` Atualizada em ${updatedAtISO}.` : "";
  const pageDescription = `Temos ${codeCount} cupons e ${offerCount} ofertas ativas hoje para ${brandName} no Shopping do Bem.${dateStr}`;

  return (
    <JsonLdScript
      data={{
        "@context": "https://schema.org",
        "@type": "AggregateRating",
        itemReviewed: {
          "@type": "Organization",
          name: brandName,
          description: pageDescription,
        },
        ratingValue: Number(ratingValue),
        ratingCount: Number(ratingCount),
        bestRating: 5,
        worstRating: 1,
      }}
    />
  );
}

/* ============================================================
 * Blog listing schema (/blog-news)
 * Emits a Blog with embedded BlogPosting items so Google can
 * surface each article as an Article rich result from the index.
 * ============================================================ */

interface BlogListingPost {
  title: string;
  slug: string;
  excerpt?: string | null;
  seo_description?: string | null;
  featured_image_url?: string | null;
  published_at?: string | null;
  updated_at?: string | null;
  author_name?: string | null;
  tags?: { name: string; slug: string }[];
}

interface BlogListingJsonLdProps {
  url: string;
  name: string;
  description: string;
  posts: BlogListingPost[];
  limit?: number;
}

export function BlogListingJsonLd({ url, name, description, posts, limit = 20 }: BlogListingJsonLdProps) {
  const items = (posts || []).slice(0, limit).map((p) => {
    const postUrl = `${SITE_URL}/blog-news/${p.slug}`;
    return {
      "@type": "BlogPosting",
      mainEntityOfPage: { "@type": "WebPage", "@id": postUrl },
      headline: p.title,
      url: postUrl,
      ...(p.featured_image_url && { image: [p.featured_image_url] }),
      ...(p.published_at && { datePublished: p.published_at }),
      ...(p.updated_at && { dateModified: p.updated_at }),
      ...(p.tags && p.tags.length > 0 && { articleSection: p.tags[0].name }),
      description: p.seo_description || p.excerpt || p.title,
      inLanguage: "pt-BR",
      author: {
        "@type": "Person",
        name: p.author_name || "Equipe Editorial",
      },
      publisher: {
        "@type": "Organization",
        name: "Shopping do Bem",
        logo: { "@type": "ImageObject", url: DEFAULT_LOGO },
      },
    };
  });

  return (
    <JsonLdScript
      data={{
        "@context": "https://schema.org",
        "@type": "Blog",
        "@id": url,
        url,
        name,
        description,
        inLanguage: "pt-BR",
        publisher: {
          "@type": "Organization",
          name: "Shopping do Bem",
          logo: { "@type": "ImageObject", url: DEFAULT_LOGO },
        },
        blogPost: items,
      }}
    />
  );
}

/* ============================================================
 * Blog post WebPage schema (/blog-news/:slug)
 * Complements ArticleJsonLd by exposing the page's H1 (name)
 * and introduction (description) plus a Speakable selector.
 * ============================================================ */

interface BlogWebPageJsonLdProps {
  url: string;
  name: string;
  description: string;
  imageUrl?: string;
  publishedAt?: string;
  modifiedAt?: string;
  breadcrumbId?: string;
}

export function BlogWebPageJsonLd({
  url,
  name,
  description,
  imageUrl,
  publishedAt,
  modifiedAt,
  breadcrumbId,
}: BlogWebPageJsonLdProps) {
  return (
    <JsonLdScript
      data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": url,
        url,
        name,
        headline: name,
        description,
        inLanguage: "pt-BR",
        isPartOf: {
          "@type": "Blog",
          "@id": `${SITE_URL}/blog-news`,
          name: "Blog & Reviews | Shopping do Bem",
          url: `${SITE_URL}/blog-news`,
        },
        ...(imageUrl && {
          primaryImageOfPage: { "@type": "ImageObject", url: imageUrl },
          image: [imageUrl],
        }),
        ...(publishedAt && { datePublished: publishedAt }),
        ...(modifiedAt && { dateModified: modifiedAt }),
        ...(breadcrumbId && { breadcrumb: { "@id": breadcrumbId } }),
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["h1", ".post-intro"],
        },
        publisher: { "@id": ORG_ID },
      }}
    />
  );
}
